import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import type { CampaignSummary } from '../../shared/types';

export function useCampaignAccess(isAuthenticated: boolean) {
  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([]);
  const [activeCampaignId, setActiveCampaignId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const loadCampaigns = useCallback(async () => {
    if (!isAuthenticated) return;
    setIsLoading(true);
    setError('');
    try {
      const data = await api.listCampaigns();
      setCampaigns(data.campaigns || []);
      setActiveCampaignId(data.activeCampaignId ?? null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to load campaigns';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  const selectCampaign = useCallback(async (campaignId: number) => {
    setError('');
    await api.selectCampaign(campaignId);
    await loadCampaigns();
  }, [loadCampaigns]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const activeCampaign = campaigns.find((campaign) => campaign.id === activeCampaignId) || null;

  return {
    campaigns,
    activeCampaignId,
    activeCampaign,
    isLoading,
    error,
    reloadCampaigns: loadCampaigns,
    selectCampaign,
  };
}
